import React, { Component } from 'react'
import { state, signal } from 'cerebral/tags'
import { connect } from '@cerebral/react'
import isAdmin from 'utils/isAdmin'
import { treeDepth, treeNodeHeight, rootNodeMargin } from 'consts'
import SortableTree, { changeNodeAtPath } from 'react-sortable-tree'
import BMSTheme from 'react-sortable-tree-theme-bms'

export default connect(
  {
    group: state`app.user.group`,
    nodes: state`node.nodes`,
    expandedList: state`node.expandedList`,
    selectedList: state`node.selectedList`,
    rebuildTree: state`node.rebuildTree`,
    mediaDragging: state`media.dragging`,
    showTreeContextMenu: signal`node.showTreeContextMenu`,
    selectNode: signal`node.selectNode`,
    onBeforeMoveNode: signal`node.onBeforeMoveNode`,
    onNodeEnter: signal`node.onNodeEnter`,
    onNodeLeave: signal`node.onNodeLeave`,
    clearRebuild: signal`node.clearRebuild`,
    toggleNodeExpanded: signal`node.toggleNodeExpanded`,
    isMenuOpen: state`node.tree.open`,
    isMenuHover: state`node.tree.hover`,
    inMenuTransition: state`node.tree.transition`
  },
  class TreeMenu extends Component {
    constructor(props) {
      super(props)

      this.renderTree()
    }

    componentDidUpdate(prevProps, prevState) {
      const { rebuildTree } = this.props
      if (rebuildTree) {
        this.renderTree()
      }
    }

    renderChildNodes(nodes, parentPath) {
      let children = []
      if (nodes) {
        children = Object.keys(nodes).map(key => {
          return this.buildNode(nodes[key], parentPath)
        })
      }

      return children
    }

    buildNode(rawNode, path, isRoot = false) {
      const { expandedList, selectedList } = this.props
      const hasNodes = Object.keys(rawNode.nodes).length > 0
      let thisPath = path + '.nodes.' + rawNode.id
      let node = {
        id: rawNode.id,
        title: rawNode.name,
        isRoot: isRoot,
        hasChildren: hasNodes,
        path: thisPath,
        expanded: expandedList[rawNode.id] || false,
        selected: selectedList[rawNode.id] || false
      }
      if (hasNodes) {
        node.children = this.renderChildNodes(rawNode.nodes, thisPath)
      }

      return node
    }

    renderRootNodes() {
      const { nodes } = this.props
      let tree = Object.keys(nodes).map((key, index) => {
        return this.buildNode(nodes[key], 'node', index !== 0)
      })

      return tree
    }

    renderTree() {
      const { clearRebuild } = this.props
      let treeData = this.renderRootNodes()

      if (this.refs.nodeTree) {
        this.setState({ treeData })
      } else {
        this.state = {
          treeData: treeData
        }
      }

      clearRebuild()
    }

    getNearestTopLeaf(nodes, currentNode) {
      let topLeaf = null
      for (let i = 0, length = nodes.length; i < length; i++) {
        if (nodes[i].id === currentNode) {
          break
        } else {
          topLeaf = nodes[i]
        }
      }

      return topLeaf
    }

    onVisibilityToggle(args) {
      const { toggleNodeExpanded } = this.props
      toggleNodeExpanded({ path: args.node.path })
    }

    onMoveNode(args) {
      const { onBeforeMoveNode } = this.props
      const parentId = args.nextParentNode ? args.nextParentNode.id : null
      const nodes = args.nextParentNode
        ? args.nextParentNode.children
        : args.treeData
      const nearestTopLeaf = this.getNearestTopLeaf(nodes, args.node.id)

      // update the database
      onBeforeMoveNode({
        node: args.node,
        parentId: parentId,
        nearestTopLeaf: nearestTopLeaf
      })
    }

    onContextMenu(e, path, depth) {
      e.preventDefault()
      e.stopPropagation()
      const { showTreeContextMenu } = this.props
      showTreeContextMenu({
        payload: {
          isVisible: true,
          position: { x: e.clientX, y: e.clientY },
          path,
          depth: depth
        }
      })
    }

    expandTreeNode(getNodeKey, node, path, toggle) {
      let expand = toggle ? !node.expanded : true
      this.setState(state => ({
        treeData: changeNodeAtPath({
          treeData: this.state.treeData,
          path,
          getNodeKey,
          newNode: { ...node, expanded: expand }
        })
      }))
    }

    render() {
      const {
        group,
        onNodeEnter,
        onNodeLeave,
        selectNode,
        toggleNodeExpanded,
        mediaDragging,
        isMenuOpen,
        isMenuHover,
        inMenuTransition
      } = this.props
      const getNodeKey = ({ node: { id } }) => id

      if (!inMenuTransition && !isMenuOpen && !isMenuHover) {
        return null
      }

      return (
        <SortableTree
          ref="nodeTree"
          innerStyle={{ outline: 'none', padding: 5 }}
          canDrag={isAdmin(group)}
          theme={BMSTheme}
          scaffoldBlockPxWidth={16}
          treeData={this.state.treeData}
          maxDepth={treeDepth}
          getNodeKey={getNodeKey}
          onChange={treeData => this.setState({ treeData })}
          rowHeight={({ node }) => {
            return treeNodeHeight + (node.isRoot ? rootNodeMargin : 0)
          }}
          onVisibilityToggle={args => {
            this.onVisibilityToggle(args)
          }}
          onMoveNode={args => {
            this.onMoveNode(args)
          }}
          generateNodeProps={({ node, path }) => {
            return {
              style: {
                fontSize: 13,
                color: node.selected ? '#0069b4' : '',
                alignSelf: 'flex-end',
                height: treeNodeHeight
              },
              onContextMenu: e => {
                this.onContextMenu(e, node.path, path.length)
              },
              onClick: e => {
                const clickedItemClassName = e.target.className
                if (
                  clickedItemClassName !== 'rstcustom__button' &&
                  clickedItemClassName !== 'expand_button' &&
                  clickedItemClassName.baseVal !== 'expand_button'
                ) {
                  // do not select node if user click on the arrow button
                  selectNode({ path: node.path, id: node.id })
                }
              },
              onDoubleClick: e => {
                if (node.hasChildren) {
                  this.expandTreeNode(getNodeKey, node, path, true)
                  toggleNodeExpanded({ path: node.path })
                }
              },
              onMouseEnter: e => {
                if (node.hasChildren && mediaDragging) {
                  this.timeout = setTimeout(() => {
                    this.expandTreeNode(getNodeKey, node, path, false)
                  }, 600)
                }

                onNodeEnter({ path: node.path })
              },
              onMouseLeave: e => {
                clearTimeout(this.timeout)
                onNodeLeave({ path: node.path })
              }
            }
          }}
        />
      )
    }
  }
)



// WEBPACK FOOTER //
// ./src/components/Menu/TreeMenu/treeMenu.js